/**
 * IK drag session stabilizer.
 *
 * Maintains state across frames during an IK drag to ensure continuity
 * and predictability. Solves two problems:
 *
 * 1. **Angular velocity clamping** — The position→quaternion conversion
 *    can amplify small perturbations into large rotation jumps, especially
 *    for short bones or configurations near singularity. Each bone's
 *    per-frame angular change is clamped to a budget proportional to the
 *    user's input displacement.
 *
 * 2. **Constraint violation detection** — When anatomical constraints
 *    significantly alter the solver's output, the session enters a
 *    "violation" state. The visual layer uses this to freeze the joint
 *    at its last valid position and display error feedback, rather than
 *    letting the joint snap to a constraint boundary.
 */

import * as THREE from "three/webgpu";
import { logSmoothingClamp } from "./ikDebugLog.ts";

// ---------------------------------------------------------------------------
// Tuning constants
// ---------------------------------------------------------------------------

/**
 * Angular budget (radians) per meter of user input displacement.
 * Tuned so that a typical screen-space drag maps to a reasonable
 * rotation rate. 1 cm of cursor movement ≈ 6° of rotation budget per bone.
 */
const ANGULAR_BUDGET_PER_METER = 2400 * (Math.PI / 180); // radians per meter of user displacement

/**
 * Minimum angular change (radians) allowed per frame even when user input
 * is nearly stationary. Lets the solver converge without getting stuck.
 */
const MIN_ANGULAR_STEP = 2.0 * (Math.PI / 180); // 2 degrees

/**
 * Maximum single-bone angular deviation (radians) between pre-constraint
 * and post-constraint quaternions that triggers violation mode.
 *
 * Set very high (90°) because the quaternion distance metric overstates
 * real violations: hinge constraints change the rotation AXIS (discarding
 * off-axis components), which registers as large angular distance even
 * when the actual angle clamping is tiny. The smoothing system already
 * prevents visual jarring from constraint corrections, so the freeze
 * should only activate for truly degenerate solver outputs.
 */
const VIOLATION_THRESHOLD = 90 * (Math.PI / 180); // 90 degrees per bone

/**
 * Once in violation, the deviation must drop below this to exit.
 * Hysteresis prevents flickering at the boundary.
 */
const VIOLATION_EXIT_THRESHOLD = 45 * (Math.PI / 180); // 45 degrees per bone

// ---------------------------------------------------------------------------
// Session state
// ---------------------------------------------------------------------------

export interface IKDragSession {
  /** Previous frame's IK target (for computing user displacement). */
  prevTarget: THREE.Vector3;
  /** Whether the session is currently in constraint-violation mode. */
  isViolating: boolean;
  /**
   * Last set of bone quaternions computed in a non-violating frame.
   * Used to freeze the skeleton when in violation.
   */
  lastValidQuats: Map<string, [number, number, number, number]>;
  /**
   * Previous frame's post-constraint quaternions. Used for violation
   * detection and lastValidQuats tracking.
   */
  prevQuats: Map<string, [number, number, number, number]>;
  /**
   * Previous frame's pre-constraint quaternions. Used as the reference
   * for angular velocity clamping in smoothQuaternions(), so that the
   * smoothing budget is measured pre-constraint-to-pre-constraint and
   * constraint clamping doesn't eat into the angular budget.
   */
  prevPreConstraintQuats: Map<string, [number, number, number, number]>;
  /** World position of the violation for visual feedback (user's cursor projected). */
  violationWorldPos: THREE.Vector3;
  /** Whether we've had at least one solve (first frame uses originals). */
  initialized: boolean;
}

// Module-level session — only one IK drag can be active at a time.
let _session: IKDragSession | null = null;

// Scratch objects (avoids per-frame allocation)
const _prevQ = new THREE.Quaternion();
const _curQ = new THREE.Quaternion();
const _smoothedQ = new THREE.Quaternion();
const _devPre = new THREE.Quaternion();
const _devPost = new THREE.Quaternion();

// ---------------------------------------------------------------------------
// Session lifecycle
// ---------------------------------------------------------------------------

/** Create a new IK drag session. Called when `enterCommand("boneTranslate")` fires. */
export function createIKSession(): void {
  _session = {
    prevTarget: new THREE.Vector3(),
    isViolating: false,
    lastValidQuats: new Map(),
    prevQuats: new Map(),
    prevPreConstraintQuats: new Map(),
    violationWorldPos: new THREE.Vector3(),
    initialized: false,
  };
}

/** Destroy the current session. Called on confirm/cancel. */
export function destroyIKSession(): void {
  _session = null;
}

/** Read-only access to the current session (for the store to expose violation state). */
export function getIKSession(): Readonly<IKDragSession> | null {
  return _session;
}

// ---------------------------------------------------------------------------
// Quaternion angular velocity clamping
// ---------------------------------------------------------------------------

/**
 * Clamp per-frame angular change of solved quaternions.
 *
 * The position→quaternion conversion (`directionsToLocalQuaternions`) can
 * amplify small position perturbations into large rotation jumps — especially
 * for short bones or configurations near singularity (e.g., a nearly-straight
 * knee). This is the main source of IK "jumpiness" on indirect bones.
 *
 * For each bone, we measure the angular distance from the previous frame's
 * quaternion and slerp toward it if the change exceeds a threshold derived
 * from the user's input displacement.
 *
 * @param quats       Current frame's solved quaternions (modified in place).
 * @param target      This frame's IK target position.
 */
export function smoothQuaternions(
  quats: Map<string, [number, number, number, number]>,
  target: THREE.Vector3,
): void {
  if (!_session || !_session.initialized) return;
  if (_session.prevPreConstraintQuats.size === 0) return;

  const userDisplacement = target.distanceTo(_session.prevTarget);
  // Angular budget: proportional to user displacement, with a floor
  const maxAngle = Math.max(
    userDisplacement * ANGULAR_BUDGET_PER_METER,
    MIN_ANGULAR_STEP,
  );

  for (const [name, cur] of quats) {
    const prev = _session.prevPreConstraintQuats.get(name);
    if (!prev) continue;

    _prevQ.set(prev[0], prev[1], prev[2], prev[3]);
    _curQ.set(cur[0], cur[1], cur[2], cur[3]);

    // Angular distance: 2 * acos(|dot|)
    const dot = Math.min(
      1,
      Math.abs(
        _prevQ.x * _curQ.x +
          _prevQ.y * _curQ.y +
          _prevQ.z * _curQ.z +
          _prevQ.w * _curQ.w,
      ),
    );
    const angle = 2 * Math.acos(dot);

    if (angle > maxAngle) {
      // Slerp from previous toward current, covering only maxAngle
      logSmoothingClamp(name, angle, maxAngle);
      const t = maxAngle / angle;
      _smoothedQ.copy(_prevQ).slerp(_curQ, t);
      _smoothedQ.normalize();
      quats.set(name, [_smoothedQ.x, _smoothedQ.y, _smoothedQ.z, _smoothedQ.w]);
    }
  }
}

// ---------------------------------------------------------------------------
// Violation detection
// ---------------------------------------------------------------------------

/**
 * Measure how much constraint clamping altered the solve.
 *
 * Returns the maximum single-bone angular deviation (radians).
 * Using max instead of sum prevents multiple small corrections (e.g. 5°+5°)
 * from falsely triggering violation mode during fast drags.
 */
export function measureConstraintDeviation(
  preConstraintQuats: Map<string, [number, number, number, number]>,
  postConstraintQuats: Map<string, [number, number, number, number]>,
): number {
  let maxDev = 0;

  for (const [name, pre] of preConstraintQuats) {
    const post = postConstraintQuats.get(name);
    if (!post) continue;

    _devPre.set(pre[0], pre[1], pre[2], pre[3]);
    _devPost.set(post[0], post[1], post[2], post[3]);

    // Angular distance: 2 * acos(|dot(a, b)|)
    const dot = Math.min(
      1,
      Math.abs(
        _devPre.x * _devPost.x +
          _devPre.y * _devPost.y +
          _devPre.z * _devPost.z +
          _devPre.w * _devPost.w,
      ),
    );
    const dev = 2 * Math.acos(dot);
    if (dev > maxDev) maxDev = dev;
  }

  return maxDev;
}

/**
 * Update the session after a solve frame. Call this after solving,
 * quaternion smoothing, and constraint application.
 *
 * - Saves current target for the next frame's angular budget computation.
 * - Detects constraint violation and enters/exits violation mode.
 * - Returns whether the caller should use `lastValidQuats` (violation freeze)
 *   or the newly computed quats.
 *
 * @param target        This frame's IK target.
 * @param violationWorldPos  World position of the user's input (for glow placement).
 * @param constraintDeviation  Result of `measureConstraintDeviation`.
 * @param newQuats      Post-constraint bone quaternions (the candidate output).
 * @param preConstraintQuats  Pre-constraint quaternions (smoothing reference for next frame).
 * @returns `true` if in violation (caller should output lastValidQuats instead).
 */
export function updateSession(
  target: THREE.Vector3,
  violationWorldPos: THREE.Vector3,
  constraintDeviation: number,
  newQuats: Map<string, [number, number, number, number]>,
  preConstraintQuats?: Map<string, [number, number, number, number]>,
): boolean {
  if (!_session) return false;

  // --- Violation detection with hysteresis ---
  if (_session.isViolating) {
    // Exit violation when deviation drops below the lower threshold
    if (constraintDeviation < VIOLATION_EXIT_THRESHOLD) {
      _session.isViolating = false;
    }
  } else {
    // Enter violation when deviation exceeds the upper threshold
    if (constraintDeviation > VIOLATION_THRESHOLD) {
      _session.isViolating = true;
    }
  }

  _session.violationWorldPos.copy(violationWorldPos);

  if (_session.isViolating) {
    // Don't update warm-start state — keep it at last-valid so the
    // solver starts from a good place when the user moves back.
    _session.prevTarget.copy(target);
    return true; // freeze
  }

  // --- Non-violating: save state for next frame ---

  _session.prevTarget.copy(target);
  _session.initialized = true;

  // Save valid quaternions
  _session.lastValidQuats.clear();
  for (const [name, q] of newQuats) {
    _session.lastValidQuats.set(name, [...q] as [
      number,
      number,
      number,
      number,
    ]);
  }

  // Save post-constraint quaternions for general tracking.
  _session.prevQuats.clear();
  for (const [name, q] of newQuats) {
    _session.prevQuats.set(name, [...q] as [number, number, number, number]);
  }

  // Save pre-constraint quaternions for next frame's angular velocity clamping.
  // Using pre-constraint values ensures the smoothing budget is measured
  // pre-constraint-to-pre-constraint, so constraint clamping doesn't eat
  // into the angular budget and cause ratcheting.
  _session.prevPreConstraintQuats.clear();
  const preQuatSource = preConstraintQuats ?? newQuats;
  for (const [name, q] of preQuatSource) {
    _session.prevPreConstraintQuats.set(name, [...q] as [
      number,
      number,
      number,
      number,
    ]);
  }

  return false; // not in violation
}
